import path from "node:path"
import type { SymbolDisplayPart } from "typescript"
import * as ts from "typescript"

import {
  Component,
  ComponentDoc,
  ComponentNameResolver,
  InterfaceOrTypeAliasDeclaration,
  JSDoc,
  Method,
  MethodParameter,
  ParentType,
  ParserOptions,
  PropFilter,
  PropItemType,
  Props,
  ResolvedType,
  ResolvedTypeKind,
  StringIndexedObject,
} from "./types.js"
import { buildFilter } from "./utils/build-filter.js"
import { isReactComponent } from "./utils/is-react-component.js"
import { trimFileName } from "./utils/trim-file-name.js"

export const defaultOptions: ts.CompilerOptions = {
  allowJs: true,
  checkJs: false,
  jsx: ts.JsxEmit.React,
  module: ts.ModuleKind.CommonJS,
  target: ts.ScriptTarget.Latest,
}

const isOptional = (prop: ts.Symbol) =>
  // tslint:disable-next-line:no-bitwise
  // eslint-disable-next-line no-bitwise
  (prop.getFlags() & ts.SymbolFlags.Optional) !== 0

const defaultJSDoc: JSDoc = {
  description: "",
  fullComment: "",
  tags: {},
}

export class Parser {
  private readonly checker: ts.TypeChecker
  private readonly propFilter: PropFilter
  private readonly savePropValueAsString: boolean
  private readonly shouldExtractLiteralValuesFromEnum: boolean
  private readonly shouldExtractValuesFromUnion: boolean
  private readonly shouldIncludeExpression: boolean
  private readonly shouldIncludePropTagMap: boolean
  private readonly shouldRemoveUndefinedFromOptional: boolean
  private readonly shouldSortUnions: boolean

  constructor(program: ts.Program, opts: ParserOptions) {
    const {
      savePropValueAsString,
      shouldExtractLiteralValuesFromEnum,
      shouldExtractValuesFromUnion,
      shouldIncludeExpression,
      shouldIncludePropTagMap,
      shouldRemoveUndefinedFromOptional,
      shouldSortUnions,
    } = opts
    this.checker = program.getTypeChecker()
    this.propFilter = buildFilter(opts)
    this.shouldExtractLiteralValuesFromEnum = Boolean(shouldExtractLiteralValuesFromEnum)
    this.shouldRemoveUndefinedFromOptional = Boolean(shouldRemoveUndefinedFromOptional)
    this.shouldExtractValuesFromUnion = Boolean(shouldExtractValuesFromUnion)
    this.shouldSortUnions = Boolean(shouldSortUnions)
    this.savePropValueAsString = Boolean(savePropValueAsString)
    this.shouldIncludePropTagMap = Boolean(shouldIncludePropTagMap)
    this.shouldIncludeExpression = Boolean(shouldIncludeExpression)
  }

  public extractDefaultPropsFromComponent(symbol: ts.Symbol, source: ts.SourceFile) {
    const possibleStatements = [
      ...source.statements
        // ensure that name property is available
        .filter(stmt => Boolean((stmt as ts.ClassDeclaration).name))
        .filter(stmt => this.checker.getSymbolAtLocation((stmt as ts.ClassDeclaration).name!) === symbol),
      ...source.statements.filter(stmt => ts.isExpressionStatement(stmt) || ts.isVariableStatement(stmt)),
    ]

    return possibleStatements.reduce((res, statement) => {
      if (statementIsClassDeclaration(statement) && statement.members.length > 0) {
        const possibleDefaultProps = statement.members.filter(
          member => member.name && getPropertyName(member.name) === "defaultProps",
        )

        if (possibleDefaultProps.length === 0) {
          return res
        }

        const defaultProps = possibleDefaultProps[0]
        let { initializer } = defaultProps as ts.PropertyDeclaration
        if (!initializer) {
          return res
        }

        let { properties } = initializer as ts.ObjectLiteralExpression

        while (ts.isIdentifier(initializer as ts.Identifier)) {
          const defaultPropsReference = this.checker.getSymbolAtLocation(initializer as ts.Node)
          if (defaultPropsReference) {
            const declarations = defaultPropsReference.getDeclarations()

            if (declarations) {
              if (ts.isImportSpecifier(declarations[0])) {
                const symbol = this.checker.getSymbolAtLocation(declarations[0].name)
                if (!symbol) {
                  continue
                }

                const aliasedSymbol = this.checker.getAliasedSymbol(symbol)
                if (aliasedSymbol && aliasedSymbol.declarations && aliasedSymbol.declarations.length > 0) {
                  initializer = (aliasedSymbol.declarations[0] as ts.VariableDeclaration).initializer
                } else {
                  continue
                }
              } else {
                initializer = (declarations[0] as ts.VariableDeclaration).initializer
              }

              properties = (initializer as ts.ObjectLiteralExpression).properties
            }
          }
        }

        let propMap = {}

        if (properties) {
          propMap = this.getPropMap(properties as ts.NodeArray<ts.PropertyAssignment>)
        }

        return {
          ...res,
          ...propMap,
        }
      }

      if (statementIsStatelessWithDefaultProps(statement)) {
        let propMap = {}
        for (const child of (statement as ts.ExpressionStatement).getChildren()) {
          let { right } = child as ts.BinaryExpression

          if (right && ts.isIdentifier(right)) {
            const value = ((source as any).locals as ts.SymbolTable).get(right.escapedText)

            if (
              value &&
              value.valueDeclaration &&
              ts.isVariableDeclaration(value.valueDeclaration) &&
              value.valueDeclaration.initializer
            ) {
              right = value.valueDeclaration.initializer
            }
          }

          if (right) {
            const { properties } = right as ts.ObjectLiteralExpression
            if (properties) {
              propMap = this.getPropMap(properties as ts.NodeArray<ts.PropertyAssignment>)
            }
          }
        }

        return {
          ...res,
          ...propMap,
        }
      }

      const functionStatement = this.getFunctionStatement(statement)

      // Extracting default values from props destructuring
      if (functionStatement && functionStatement.parameters && functionStatement.parameters.length > 0) {
        const { name } = functionStatement.parameters[0]

        if (ts.isObjectBindingPattern(name)) {
          return {
            ...res,
            ...this.getPropMap(name.elements),
          }
        }
      }

      return res
    }, {})
  }

  public extractMembersFromType(type: ts.Type): ts.Symbol[] {
    const methodSymbols: ts.Symbol[] = []

    /**
     * Need to loop over properties first so we capture any
     * static methods. static methods aren't captured in type.symbol.members
     */
    for (const property of type.getProperties()) {
      // Only add members, don't add non-member properties
      if (this.getCallSignature(property)) {
        methodSymbols.push(property)
      }
    }

    if (type.symbol && type.symbol.members) {
      for (const [_, member] of type.symbol.members) {
        methodSymbols.push(member)
      }
    }

    return methodSymbols
  }

  public extractPropsFromTypeIfStatefulComponent(type: ts.Type): null | ts.Symbol {
    const constructSignatures = type.getConstructSignatures()

    if (constructSignatures.length > 0) {
      // React.Component. Is a class, so the props object we're interested
      // in is the type of 'props' property of the object constructed by the class.

      for (const sig of constructSignatures) {
        const instanceType = sig.getReturnType()

        const props = instanceType.getProperty("props")

        if (props) {
          return props
        }
      }
    }

    return null
  }

  public extractPropsFromTypeIfStatelessComponent(type: ts.Type): null | ts.Symbol {
    const callSignatures = type.getCallSignatures()

    if (callSignatures.length > 0) {
      // Could be a stateless component.  Is a function, so the props object we're interested
      // in is the (only) parameter.

      for (const sig of callSignatures) {
        const params = sig.getParameters()
        if (params.length === 0) {
          continue
        }

        // Maybe we could check return type instead,
        // but not sure if Element, ReactElement<T> are all possible values
        const propsParam = params[0]
        if (propsParam.name === "props" || params.length === 1) {
          return propsParam
        }
      }
    }

    return null
  }

  public findDocComment(symbol: ts.Symbol): JSDoc {
    const comment = this.getFullJsDocComment(symbol)
    if (comment.fullComment || comment.tags.default) {
      return comment
    }

    const rootSymbols = this.checker.getRootSymbols(symbol)
    const commentsOnRootSymbols = rootSymbols
      .filter(x => x !== symbol)
      .map(x => this.getFullJsDocComment(x))
      .filter(x => Boolean(x.fullComment) || Boolean(comment.tags.default))

    if (commentsOnRootSymbols.length > 0) {
      return commentsOnRootSymbols[0]
    }

    return defaultJSDoc
  }

  public getCallSignature(symbol: ts.Symbol) {
    const symbolType = this.checker.getTypeOfSymbolAtLocation(symbol, symbol.valueDeclaration!)

    return symbolType.getCallSignatures()[0]
  }

  public getComponentInfo(
    exp: ts.Symbol,
    source: ts.SourceFile,
    componentNameResolver: ComponentNameResolver = () => undefined,
    customComponentTypes: ParserOptions["customComponentTypes"] = [],
  ): ComponentDoc | null {
    const rootExportName = exp.getName()
    if (exp.declarations && exp.declarations.length === 0) {
      return null
    }

    let rootExp = this.getComponentFromExpression(exp)
    const declaration = rootExp.valueDeclaration || rootExp.declarations![0]
    const type = this.checker.getTypeOfSymbolAtLocation(rootExp, declaration)

    let commentSource = rootExp
    const typeSymbol = type.symbol || type.aliasSymbol
    const originalName = rootExp.getName()

    // get file path of aliased symbol. E.g. when there is named export we want to get filename of exported symbol not the file that declares the named export
    const filePath =
      exp.flags & ts.SymbolFlags.Alias
        ? this.checker.getAliasedSymbol(exp).declarations?.[0]?.getSourceFile().fileName || source.fileName
        : source.fileName

    if (!rootExp.valueDeclaration) {
      if (!typeSymbol && (rootExp.flags & ts.SymbolFlags.Alias) !== 0) {
        commentSource = this.checker.getAliasedSymbol(commentSource)
      } else if (typeSymbol) {
        rootExp = typeSymbol
        const expName = rootExp.getName()

        const defaultComponentTypes = [
          "__function",
          "StatelessComponent",
          "Stateless",
          "StyledComponentClass",
          "StyledComponent",
          "IStyledComponent",
          "FunctionComponent",
          "ForwardRefExoticComponent",
          "MemoExoticComponent",
        ]

        const supportedComponentTypes = [...defaultComponentTypes, ...customComponentTypes]

        commentSource = supportedComponentTypes.includes(expName)
          ? this.checker.getAliasedSymbol(commentSource)
          : rootExp
      } else {
        return null
      }
    } else if (type.symbol && (ts.isPropertyAccessExpression(declaration) || ts.isPropertyDeclaration(declaration))) {
      commentSource = type.symbol
    }

    // Skip over PropTypes that are exported
    if (typeSymbol && (typeSymbol.getEscapedName() === "Requireable" || typeSymbol.getEscapedName() === "Validator")) {
      return null
    }

    const propsType =
      this.extractPropsFromTypeIfStatelessComponent(type) || this.extractPropsFromTypeIfStatefulComponent(type)

    const nameSource = originalName === "default" ? rootExp : commentSource

    const resolvedComponentName = componentNameResolver(nameSource, source)
    const { description, tags } = this.findDocComment(commentSource)

    const exportName = computeComponentExportName(nameSource, source, customComponentTypes, rootExportName)

    const displayName = resolvedComponentName || tags.visibleName || computeComponentDisplayName(nameSource, source)

    const methods = this.getMethodsInfo(type)

    let result: ComponentDoc | null = null

    if (propsType) {
      // if there is no value declaration then don't pick up default props
      const defaultProps = commentSource.valueDeclaration
        ? this.extractDefaultPropsFromComponent(commentSource, commentSource.valueDeclaration.getSourceFile())
        : {}
      const props = this.getPropsInfo(propsType, defaultProps)

      for (const propName of Object.keys(props)) {
        const prop = props[propName]
        const component: Component = { name: exportName }
        if (!this.propFilter(prop, component)) {
          delete props[propName]
        }
      }

      result = {
        description,
        displayName,
        exportName,
        filePath,
        methods,
        props,
        tags,
      }
    } else if (exportName) {
      result = {
        description,
        displayName,
        exportName,
        filePath,
        methods,
        props: {},
        tags,
      }
    }

    if (result !== null && this.shouldIncludeExpression) {
      result.expression = rootExp
      result.rootExpression = exp
    }

    return result
  }

  /**
   * Gets the TypeScript type structure for a given type
   */
  public getResolvedType({
    propType,
    skipUndefinedInUnion,
    visitedTypes = new Set(),
  }: {
    propType: ts.Type
    skipUndefinedInUnion?: boolean
    visitedTypes?: Set<string>
  }): ResolvedType {
    const typeString = this.checker.typeToString(propType)
    const raw = typeString

    // Prevent infinite recursion
    if (visitedTypes.has(typeString)) {
      return { kind: ResolvedTypeKind.Any, raw }
    }

    // Add current type to visited set
    const newVisitedTypes = new Set(visitedTypes)
    newVisitedTypes.add(typeString)

    // Handle function types
    if (propType.getCallSignatures().length > 0) {
      return { kind: ResolvedTypeKind.Function, raw }
    }

    // Handle React node types
    if (/^(ReactNode|ReactElement|Element)<?.*$/i.test(typeString)) {
      return { kind: ResolvedTypeKind.Slot, raw }
    }

    // we need to check if the type is not boolean because boolean is recognized as union
    if (propType.isUnion() && (propType.flags & ts.TypeFlags.Boolean) === 0) {
      const types = propType.types
        .filter(type => {
          if (skipUndefinedInUnion && type.flags & ts.TypeFlags.Undefined) {
            return false
          }

          return true
        })
        .map(subType => this.getResolvedType({ propType: subType, visitedTypes: newVisitedTypes }))
        .filter((type): type is ResolvedType => type !== null)

      if (types.length === 1) {
        return types[0]
      }

      return {
        kind: ResolvedTypeKind.Union,
        raw: skipUndefinedInUnion ? raw.replaceAll(" | undefined", "") : raw,
        types,
      }
    }

    if (propType.isIntersection()) {
      const isEverySubTypeObject = propType.types.every(subType => subType.flags & ts.TypeFlags.Object)

      if (isEverySubTypeObject) {
        return {
          kind: ResolvedTypeKind.Object,
          raw,
        }
      }

      return {
        kind: ResolvedTypeKind.Any,
        raw,
      }
    }

    // Handle array types
    if (this.checker.isArrayType(propType)) {
      // Get the element type of the array
      const elementType = this.checker.getTypeArguments(propType as ts.TypeReference)[0]
      const baseType = this.getResolvedType({ propType: elementType, visitedTypes: newVisitedTypes })
      return {
        ...baseType,
        raw,
        isArray: true,
      }
    }

    // literal string type e.g. "Hello", "World"
    if (propType.isStringLiteral()) {
      return { kind: ResolvedTypeKind.StringLiteral, raw: raw.replaceAll('"', "") }
    }

    // Handle primitive types
    if ((propType.flags & ts.TypeFlags.String) !== 0) {
      return { kind: ResolvedTypeKind.String, raw }
    }

    // literal number type e.g. 1, 2, 3
    if (propType.isNumberLiteral()) {
      return { kind: ResolvedTypeKind.NumberLiteral, raw }
    }

    if ((propType.flags & ts.TypeFlags.Number) !== 0) {
      return { kind: ResolvedTypeKind.Number, raw }
    }

    // literal type, e.g. true, false
    if ((propType.flags & ts.TypeFlags.BooleanLiteral) !== 0) {
      return { kind: ResolvedTypeKind.BooleanLiteral, raw }
    }

    if ((propType.flags & ts.TypeFlags.Boolean) !== 0) {
      return { kind: ResolvedTypeKind.Boolean, raw }
    }

    if ((propType.flags & ts.TypeFlags.Null) !== 0) {
      return { kind: ResolvedTypeKind.Null, raw }
    }

    if ((propType.flags & ts.TypeFlags.Undefined) !== 0) {
      return { kind: ResolvedTypeKind.Undefined, raw }
    }

    // Handle object types
    if ((propType.flags & ts.TypeFlags.Object) !== 0) {
      const rawDeclaration = propType.symbol?.declarations?.[0]?.getText()
      const isDeclarationFromReact = propType.symbol?.declarations?.[0]
        ?.getSourceFile()
        .fileName.includes("@types/react")

      return { kind: ResolvedTypeKind.Object, raw: rawDeclaration && !isDeclarationFromReact ? rawDeclaration : raw }
    }

    return { kind: ResolvedTypeKind.Any, raw }
  }

  /**
   * This is implementation of original docgen type resolving. We have custom resolving of types right now so this is not needed.
   */
  public getDocgenType(propType: ts.Type, isRequired: boolean): PropItemType {
    // When we are going to process the type, we check if this type has a constraint (is a generic type with constraint)
    if (propType.getConstraint()) {
      // If so, we assing the property the type that is the constraint
      propType = propType.getConstraint()!
    }

    let propTypeString = this.checker.typeToString(propType)
    if (this.shouldRemoveUndefinedFromOptional && !isRequired) {
      propTypeString = propTypeString.replace(" | undefined", "")
    }

    if (
      propType.isUnion() &&
      (this.shouldExtractValuesFromUnion ||
        (this.shouldExtractLiteralValuesFromEnum &&
          propType.types.every(
            type =>
              type.getFlags() &
              (ts.TypeFlags.StringLiteral |
                ts.TypeFlags.NumberLiteral |
                ts.TypeFlags.EnumLiteral |
                ts.TypeFlags.Undefined),
          )))
    ) {
      let value = propType.types.map(type => this.getInfoFromUnionType(type))

      if (this.shouldRemoveUndefinedFromOptional && !isRequired) {
        value = value.filter(option => option.value != "undefined")
      }

      if (this.shouldSortUnions) {
        value.sort((a, b) => a.value.toString().localeCompare(b.value.toString()))
      }

      return {
        name: "enum",
        raw: propTypeString,
        value,
      }
    }

    if (this.shouldRemoveUndefinedFromOptional && !isRequired) {
      propTypeString = propTypeString.replace(" | undefined", "")
    }

    return { name: propTypeString }
  }

  /**
   * Extracts a full JsDoc comment from a symbol, even
   * though TypeScript has broken down the JsDoc comment into plain
   * text and JsDoc tags.
   */
  public getFullJsDocComment(symbol: ts.Symbol): JSDoc {
    // in some cases this can be undefined (Pick<Type, 'prop1'|'prop2'>)
    if (symbol.getDocumentationComment === undefined) {
      return defaultJSDoc
    }

    let mainComment = ts.displayPartsToString(symbol.getDocumentationComment(this.checker))

    if (mainComment) {
      mainComment = mainComment.replaceAll("\r\n", "\n")
    }

    const tags = symbol.getJsDocTags() || []

    const tagComments: string[] = []
    const tagMap: StringIndexedObject<string> = {}

    for (const tag of tags) {
      const trimmedText = ts.displayPartsToString(tag.text).trim()
      const currentValue = tagMap[tag.name]
      tagMap[tag.name] = currentValue ? currentValue + "\n" + trimmedText : trimmedText

      if (!["default", "type"].includes(tag.name)) {
        tagComments.push(formatTag(tag))
      }
    }

    return {
      description: mainComment,
      fullComment: (mainComment + "\n" + tagComments.join("\n")).trim(),
      tags: tagMap,
    }
  }

  getFunctionStatement(statement: ts.Statement) {
    if (ts.isFunctionDeclaration(statement)) {
      return statement
    }

    if (ts.isVariableStatement(statement)) {
      let initializer = statement.declarationList && statement.declarationList.declarations[0].initializer

      // Look at forwardRef function argument
      if (initializer && ts.isCallExpression(initializer)) {
        const symbol = this.checker.getSymbolAtLocation(initializer.expression)
        if (!symbol || symbol.getName() !== "forwardRef") return
        initializer = initializer.arguments[0]
      }

      if (initializer && (ts.isArrowFunction(initializer) || ts.isFunctionExpression(initializer))) {
        return initializer
      }
    }

    return undefined
  }

  public getLiteralValueFromImportSpecifier(
    property: ts.ImportSpecifier,
  ): boolean | null | number | string | undefined {
    if (ts.isImportSpecifier(property)) {
      const symbol = this.checker.getSymbolAtLocation(property.name)

      if (!symbol) {
        return null
      }

      const aliasedSymbol = this.checker.getAliasedSymbol(symbol)
      if (aliasedSymbol && aliasedSymbol.declarations && aliasedSymbol.declarations.length > 0) {
        return this.getLiteralValueFromPropertyAssignment(aliasedSymbol.declarations[0] as ts.BindingElement)
      }

      return null
    }

    return null
  }

  public getLiteralValueFromPropertyAssignment(
    property: ts.BindingElement | ts.PropertyAssignment,
  ): boolean | null | number | string | undefined {
    let { initializer } = property

    // Shorthand properties, so inflect their actual value
    if (!initializer && ts.isShorthandPropertyAssignment(property)) {
      const symbol = this.checker.getShorthandAssignmentValueSymbol(property)
      const decl = symbol && (symbol.valueDeclaration as ts.VariableDeclaration)

      if (decl && decl.initializer) {
        initializer = decl.initializer!
      }
    }

    if (!initializer) {
      return undefined
    }

    // Literal values
    switch (initializer.kind) {
      case ts.SyntaxKind.FalseKeyword: {
        return this.savePropValueAsString ? "false" : false
      }

      case ts.SyntaxKind.Identifier: {
        if ((initializer as ts.Identifier).text === "undefined") {
          return "undefined"
        }

        const symbol = this.checker.getSymbolAtLocation(initializer as ts.Identifier)

        if (symbol && symbol.declarations && symbol.declarations.length > 0) {
          if (ts.isImportSpecifier(symbol.declarations[0])) {
            return this.getLiteralValueFromImportSpecifier(symbol.declarations[0] as ts.ImportSpecifier)
          }

          return this.getLiteralValueFromPropertyAssignment(symbol.declarations[0] as ts.BindingElement)
        }

        return null
      }

      case ts.SyntaxKind.NullKeyword: {
        return this.savePropValueAsString ? "null" : null
      }

      case ts.SyntaxKind.NumericLiteral: {
        return this.savePropValueAsString
          ? `${(initializer as ts.NumericLiteral).text}`
          : Number((initializer as ts.NumericLiteral).text)
      }

      case ts.SyntaxKind.PrefixUnaryExpression: {
        return this.savePropValueAsString
          ? initializer.getFullText().trim()
          : Number((initializer as ts.PrefixUnaryExpression).getFullText())
      }

      case ts.SyntaxKind.StringLiteral: {
        return (initializer as ts.StringLiteral).text.trim()
      }

      case ts.SyntaxKind.TrueKeyword: {
        return this.savePropValueAsString ? "true" : true
      }

      case ts.SyntaxKind.PropertyAccessExpression: {
        const symbol = this.checker.getSymbolAtLocation(initializer as ts.PropertyAccessExpression)

        if (symbol && symbol.declarations && symbol.declarations.length > 0) {
          const declaration = symbol.declarations[0]

          if (ts.isBindingElement(declaration) || ts.isPropertyAssignment(declaration)) {
            return this.getLiteralValueFromPropertyAssignment(declaration)
          }
        }

        return null
      }

      case ts.SyntaxKind.ObjectLiteralExpression:
      default: {
        try {
          return initializer.getText()
        } catch {
          return null
        }
      }
    }
  }

  public getMethodsInfo(type: ts.Type): Method[] {
    const members = this.extractMembersFromType(type)
    const methods: Method[] = []
    for (const member of members) {
      if (!this.isTaggedPublic(member)) {
        continue
      }

      const name = member.getName()
      const docblock = this.getFullJsDocComment(member).fullComment
      const callSignature = this.getCallSignature(member)
      const params = this.getParameterInfo(callSignature)
      const description = ts.displayPartsToString(member.getDocumentationComment(this.checker))
      const returnType = this.checker.typeToString(callSignature.getReturnType())
      const returnDescription = ts.displayPartsToString(this.getReturnDescription(member))
      const modifiers = this.getModifiers(member)

      methods.push({
        description,
        docblock,
        modifiers,
        name,
        params,
        returns: returnDescription
          ? {
              description: returnDescription,
              type: returnType,
            }
          : null,
      })
    }

    return methods
  }

  public getModifiers(member: ts.Symbol) {
    const modifiers: string[] = []
    if (!member.valueDeclaration) {
      return modifiers
    }

    const flags = ts.getCombinedModifierFlags(member.valueDeclaration)
    const isStatic = (flags & ts.ModifierFlags.Static) !== 0 // tslint:disable-line no-bitwise

    if (isStatic) {
      modifiers.push("static")
    }

    return modifiers
  }

  public getParameterInfo(callSignature: ts.Signature): MethodParameter[] {
    return callSignature.parameters.map(param => {
      const paramType = this.checker.getTypeOfSymbolAtLocation(param, param.valueDeclaration!)
      const paramDeclaration = this.checker.symbolToParameterDeclaration(param, undefined, undefined)
      const isOptionalParam: boolean = Boolean(paramDeclaration && paramDeclaration.questionToken)

      return {
        description: ts.displayPartsToString(param.getDocumentationComment(this.checker)) || null,
        name: param.getName() + (isOptionalParam ? "?" : ""),
        type: { name: this.checker.typeToString(paramType) },
      }
    })
  }

  public getPropMap(
    properties: ts.NodeArray<ts.BindingElement | ts.PropertyAssignment>,
  ): StringIndexedObject<boolean | null | number | string> {
    return properties.reduce(
      (acc, property) => {
        if (ts.isSpreadAssignment(property) || !property.name) {
          return acc
        }

        const literalValue = this.getLiteralValueFromPropertyAssignment(property)
        const propertyName = getPropertyName(property.name)

        if (
          (typeof literalValue === "string" ||
            typeof literalValue === "number" ||
            typeof literalValue === "boolean" ||
            literalValue === null) &&
          propertyName !== null
        ) {
          acc[propertyName] = literalValue
        }

        return acc
      },
      {} as StringIndexedObject<boolean | null | number | string>,
    )
  }

  public getPropsInfo(propsObj: ts.Symbol, defaultProps: StringIndexedObject<string> = {}): Props {
    const propsDeclaration = propsObj.valueDeclaration ?? propsObj.getDeclarations()?.[0]

    if (!propsDeclaration) {
      return {}
    }

    const propsType = this.checker.getTypeOfSymbolAtLocation(propsObj, propsDeclaration)
    const baseProps = propsType.getApparentProperties()
    let propertiesOfProps = baseProps

    if (propsType.isUnionOrIntersection()) {
      propertiesOfProps = [
        // Resolve extra properties in the union/intersection
        ...(propertiesOfProps = (this.checker as any).getAllPossiblePropertiesOfTypes(propsType.types)),
        // But props we already have override those as they are already correct.
        ...baseProps,
      ]

      if (propertiesOfProps.length === 0) {
        const isUnionType = (type: ts.Type): boolean => (type.flags & ts.TypeFlags.Union) !== 0

        const types: ts.Type[] = isUnionType(propsType) ? (propsType as ts.UnionType).types : [propsType]

        const subTypes = (this.checker as any).getAllPossiblePropertiesOfTypes(
          types.reduce<ts.Symbol[]>((all, t) => {
            const typeArray = isUnionType(t) ? (t as ts.UnionType).types : [t]
            return [...all, ...(typeArray.map(t => t.symbol!).filter(s => s !== undefined) as ts.Symbol[])]
          }, []),
        )

        propertiesOfProps = [...subTypes, ...baseProps]
      }
    }

    const result: Props = {}

    for (const prop of propertiesOfProps) {
      const propName = prop.getName()

      // Find type of prop by looking in context of the props object itself.
      const propType = this.checker.getTypeOfSymbolAtLocation(prop, propsDeclaration)

      const jsDocComment = this.findDocComment(prop)
      const hasCodeBasedDefault = defaultProps[propName] !== undefined

      let defaultValue: null | { value: any } = null

      if (hasCodeBasedDefault) {
        defaultValue = { value: defaultProps[propName] }
      } else if (jsDocComment.tags.default) {
        defaultValue = { value: jsDocComment.tags.default }
      }

      const parent = getParentType(prop)
      const parents = getDeclarations(prop)
      const declarations = prop.declarations || []

      const baseProp = baseProps.find(p => p.getName() === propName)

      const required =
        !isOptional(prop) &&
        !hasCodeBasedDefault &&
        // If in a intersection or union check original declaration for "?"
        declarations.every(d => !((ts.isPropertySignature(d) || ts.isPropertyDeclaration(d) || ts.isParameter(d)) && d.questionToken)) &&
        (!baseProp || !isOptional(baseProp))

      const type = jsDocComment.tags.type
        ? {
            name: jsDocComment.tags.type,
          }
        : this.getDocgenType(propType, required)

      const propTags = this.shouldIncludePropTagMap ? { tags: jsDocComment.tags } : {}
      const description = this.shouldIncludePropTagMap
        ? jsDocComment.description.replaceAll("\r\n", "\n")
        : jsDocComment.fullComment.replaceAll("\r\n", "\n")

      const resolvedType = this.getResolvedType({ propType, skipUndefinedInUnion: !required })

      result[propName] = {
        resolvedType,
        declarations: parents,
        defaultValue,
        description,
        name: propName,
        parent,
        required,
        type,
        ...propTags,
      }
    }

    return result
  }

  public getReturnDescription(symbol: ts.Symbol): SymbolDisplayPart[] | undefined {
    const tags = symbol.getJsDocTags()
    const returnTag = tags.find(tag => tag.name === "returns")
    if (!returnTag || !Array.isArray(returnTag.text)) {
      return
    }

    return returnTag.text
  }

  public isTaggedPublic(symbol: ts.Symbol) {
    const jsDocTags = symbol.getJsDocTags()
    return Boolean(jsDocTags.find(tag => tag.name === "public"))
  }

  /**
   * Parses a symbol that is an export from a source file.
   */
  public parseExportSymbol(exp: ts.Symbol, sourceFile: ts.SourceFile) {
    if (!isReactComponent(exp, sourceFile, this.checker)) {
      return []
    }

    const componentDocs: ComponentDoc[] = []
    const doc = this.getComponentInfo(exp, sourceFile)

    if (doc) {
      componentDocs.push(doc)
    }

    if (!exp.exports) {
      return componentDocs
    }

    // Then document any static sub-components
    for (const [_, symbol] of exp.exports) {
      if (symbol.flags & ts.SymbolFlags.Prototype) {
        continue
      }

      if (!isReactComponent(symbol, sourceFile, this.checker)) {
        continue
      }

      if (symbol.flags & ts.SymbolFlags.Method) {
        const signature = this.getCallSignature(symbol)
        const returnType = this.checker.typeToString(signature.getReturnType())

        if (returnType !== "Element") {
          continue
        }
      }

      const doc = this.getComponentInfo(symbol, sourceFile)

      if (doc) {
        const prefix = exp.escapedName === "default" ? "" : `${exp.escapedName}.`

        componentDocs.push({
          ...doc,
          displayName: `${prefix}${symbol.escapedName}`,
        })
      }
    }

    return componentDocs
  }

  private getComponentFromExpression(exp: ts.Symbol) {
    const declaration = exp.valueDeclaration || exp.declarations![0]
    const type = this.checker.getTypeOfSymbolAtLocation(exp, declaration)
    const typeSymbol = type.symbol || type.aliasSymbol

    if (!typeSymbol) {
      return exp
    }

    const symbolName = typeSymbol.getName()

    if (
      (symbolName === "MemoExoticComponent" || symbolName === "ForwardRefExoticComponent") &&
      exp.valueDeclaration &&
      ts.isExportAssignment(exp.valueDeclaration) &&
      ts.isCallExpression(exp.valueDeclaration.expression)
    ) {
      const component = this.checker.getSymbolAtLocation(exp.valueDeclaration.expression.arguments[0])

      if (component) {
        exp = component
      }
    }

    return exp
  }

  private getInfoFromUnionType(type: ts.Type): Partial<JSDoc> & {
    value: number | string
  } {
    let commentInfo = {}
    if (type.getSymbol()) {
      commentInfo = { ...this.getFullJsDocComment(type.getSymbol()!) }
    }

    return {
      value: this.getValuesFromUnionType(type),
      ...commentInfo,
    }
  }

  private getValuesFromUnionType(type: ts.Type): number | string {
    if (type.isStringLiteral()) return `"${type.value}"`
    if (type.isNumberLiteral()) return `${type.value}`
    return this.checker.typeToString(type)
  }
}

function statementIsClassDeclaration(statement: ts.Statement): statement is ts.ClassDeclaration {
  return Boolean((statement as ts.ClassDeclaration).members)
}

function statementIsStatelessWithDefaultProps(statement: ts.Statement): boolean {
  const children = (statement as ts.ExpressionStatement).getChildren()
  for (const child of children) {
    const { left } = child as ts.BinaryExpression
    if (left) {
      const { name } = left as ts.PropertyAccessExpression
      if (name && name.escapedText === "defaultProps") {
        return true
      }
    }
  }

  return false
}

function getPropertyName(name: ts.BindingPattern | ts.PropertyName): null | string {
  switch (name.kind) {
    case ts.SyntaxKind.ComputedPropertyName: {
      return name.getText()
    }

    case ts.SyntaxKind.Identifier:
    case ts.SyntaxKind.NumericLiteral:
    case ts.SyntaxKind.StringLiteral: {
      return name.text
    }

    default: {
      return null
    }
  }
}

function formatTag(tag: ts.JSDocTagInfo) {
  let result = "@" + tag.name
  if (tag.text) {
    result += " " + ts.displayPartsToString(tag.text)
  }

  return result
}

function getTextValueOfClassMember(classDeclaration: ts.ClassDeclaration, memberName: string): string {
  const classDeclarationMembers = classDeclaration.members || []
  const [textValue] =
    classDeclarationMembers &&
    classDeclarationMembers
      .filter(member => ts.isPropertyDeclaration(member))
      .filter(member => {
        const name = ts.getNameOfDeclaration(member) as ts.Identifier
        return name && name.text === memberName
      })
      .map(member => {
        const property = member as ts.PropertyDeclaration
        return property.initializer && (property.initializer as ts.Identifier).text
      })

  return textValue || ""
}

function getTextValueOfFunctionProperty(_exp: ts.Symbol, source: ts.SourceFile, propertyName: string) {
  const [textValue] = source.statements
    .filter(statement => ts.isExpressionStatement(statement))
    .filter(statement => {
      const expr = (statement as ts.ExpressionStatement).expression as ts.BinaryExpression
      return (
        expr.left &&
        (expr.left as ts.PropertyAccessExpression).name &&
        (expr.left as ts.PropertyAccessExpression).name.escapedText === propertyName
      )
    })
    .filter(statement =>
      ts.isStringLiteral(((statement as ts.ExpressionStatement).expression as ts.BinaryExpression).right),
    )
    .map(
      statement =>
        (((statement as ts.ExpressionStatement).expression as ts.BinaryExpression).right as ts.Identifier).text,
    )

  return textValue || ""
}

function computeComponentDisplayName(exp: ts.Symbol, source: ts.SourceFile) {
  const statelessDisplayName = getTextValueOfFunctionProperty(exp, source, "displayName")

  const statefulDisplayName =
    exp.valueDeclaration &&
    ts.isClassDeclaration(exp.valueDeclaration) &&
    getTextValueOfClassMember(exp.valueDeclaration, "displayName")

  return statelessDisplayName || statefulDisplayName || null
}

function computeComponentExportName(
  rootExport: ts.Symbol,
  source: ts.SourceFile,
  customComponentTypes: ParserOptions["customComponentTypes"] = [],
  rootExportName: string,
) {
  const exportName = rootExport.getName()

  const defaultComponentTypes = [
    "default",
    "__function",
    "Stateless",
    "StyledComponentClass",
    "StyledComponent",
    "IStyledComponent",
    "FunctionComponent",
    "StatelessComponent",
    "ForwardRefExoticComponent",
    "MemoExoticComponent",
  ]

  const supportedComponentTypes = [...defaultComponentTypes, ...customComponentTypes]

  if (!defaultComponentTypes.includes(rootExportName)) {
    return rootExportName
  }

  if (supportedComponentTypes.includes(exportName)) {
    return getDefaultExportForFile(source)
  }

  return exportName
}

// Default export for a file: named after file
export function getDefaultExportForFile(source: ts.SourceFile) {
  const name = path.basename(source.fileName).split(".")[0]
  const filename = name === "index" ? path.basename(path.dirname(source.fileName)) : name

  // JS identifiers must starts with a letter, and contain letters and/or numbers
  // So, you could not take filename as is
  const identifier = filename.replaceAll(/^[^A-Z]*/gi, "").replaceAll(/[^A-Z0-9]*/gi, "")

  return identifier.length > 0 ? identifier : "DefaultName"
}

function isTypeLiteral(node: ts.Node): node is ts.TypeLiteralNode {
  return node.kind === ts.SyntaxKind.TypeLiteral
}

function getDeclarations(prop: ts.Symbol): ParentType[] | undefined {
  const declarations = prop.getDeclarations()

  if (declarations === undefined || declarations.length === 0) {
    return undefined
  }

  const parents: ParentType[] = []

  for (const declaration of declarations) {
    const { parent } = declaration

    if (!isTypeLiteral(parent) && !isInterfaceOrTypeAliasDeclaration(parent)) {
      continue
    }

    const parentName = "name" in parent ? (parent as InterfaceOrTypeAliasDeclaration).name.text : "TypeLiteral"

    const { fileName } = (parent as InterfaceOrTypeAliasDeclaration | ts.TypeLiteralNode).getSourceFile()

    parents.push({
      fileName,
      name: parentName,
    })
  }

  return parents
}

function getParentType(prop: ts.Symbol): ParentType | undefined {
  const declarations = prop.getDeclarations()

  if (declarations == null || declarations.length === 0) {
    return undefined
  }

  // Props can be declared only in one place
  const { parent } = declarations[0]

  if (!isInterfaceOrTypeAliasDeclaration(parent)) {
    return undefined
  }

  const parentName = parent.name.text
  const { fileName } = parent.getSourceFile()

  return {
    fileName: trimFileName(fileName),
    name: parentName,
  }
}

function isInterfaceOrTypeAliasDeclaration(node: ts.Node): node is ts.InterfaceDeclaration | ts.TypeAliasDeclaration {
  return node.kind === ts.SyntaxKind.InterfaceDeclaration || node.kind === ts.SyntaxKind.TypeAliasDeclaration
}
